'use client';
import { Box, Button, IconButton, Stack, Typography, useBreakpoints } from '@castlery/fortress';
import { ArrowRight, ThreeDRotation, ViewInAr } from '@castlery/fortress/Icons';
import { Product, Variant, selectBundleVariants, selectProduct, selectVariant } from '@castlery/modules-product-domain';
import { mergeAndSortArrays, handleImagesSort } from '@castlery/modules-product-services';
import { PinchZoomViewer } from '@castlery/shared-components';
import { useSelector } from '@castlery/shared-redux-store';
import { usePathname, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { default as ReactPlayerType } from 'react-player';
import { logger } from '@castlery/observability';
import type { Swiper as SwiperType } from 'swiper';
import { FreeMode, Mousewheel, Navigation, Scrollbar, Thumbs } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { useCompatibility } from '../../../hooks/use-compatibility';
import { useHash } from '../../../hooks/use-hash';
import { useSlickAutoScroll } from '../../image-gallery/hooks/use-slick-auto-scroll';
import { ARDrawer } from '../../threed-ar/ar/ar-drawer';
import { SketchfabViewer } from '../../threed-ar/sketchfab-viewer/sketchfab-viewer';
import { MediaItem } from './components/media-item';
import { ThumbnailImage } from './components/thumbnail-image';
import { useAppDispatch } from '@castlery/shared-redux-store';
import { EVENT_PDP_IMAGE_IMPRESSION, EVENT_PDP_DETAILS } from '@castlery/modules-tracking-services';
import { useFirstInView } from '@castlery/modules-tracking-components';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/mousewheel';
import 'swiper/css/navigation';
import 'swiper/css/scrollbar';
import 'swiper/css/thumbs';
import { SwiperArrow } from './components/swiper-arrow';

// 主组件
export function RefinedProductGalleryClient() {
  const galleryRef = useRef<HTMLDivElement>(null);
  const mainSwiperRef = useRef<SwiperType | null>(null);
  const thumbsSwiperRef = useRef<SwiperType | null>(null);
  const videoRefs = useRef<Map<number, ReactPlayerType & { handleClickPreview?: () => void }>>(new Map());
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [touchStartTime, setTouchStartTime] = useState(0);
  const [touchEndTime, setTouchEndTime] = useState(0);
  // const [playingVideos, setPlayingVideos] = useState<Set<number>>(new Set());
  const [dimensionPinchZoomActive, setDimensionPinchZoomActive] = useState(false);
  const [dimensionActiveSet, setDimensionActiveSet] = useState<Set<number>>(new Set());
  const [dimensionPinchZoomOpen, setDimensionPinchZoomOpen] = useState(false);
  // AR/3D 相关状态
  const [showThreeD, setShowThreeD] = useState(false);
  const [showARDrawer, setShowARDrawer] = useState(false);
  const [defaultStartAR, setDefaultStartAR] = useState(false);
  const [currentUrl, setCurrentUrl] = useState('');
  const [showThumbnails, setShowThumbnails] = useState(false);
  const { desktop, tablet, mobile } = useBreakpoints();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const dispatch = useAppDispatch();

  const hash = useHash();
  const product = useSelector(selectProduct) as Product;
  const selectedVariant: Variant = useSelector(selectVariant) || product?.variants?.[0];
  const bundleVariants = useSelector(selectBundleVariants);
  const { supports3D, supportsAR } = useCompatibility();

  const [open, setOpen] = useState(false);

  const uid = useMemo(() => {
    return selectedVariant?.sketchfab_3d_model_id?.trim();
  }, [selectedVariant]);

  const isSupportThreeD = useMemo(() => Boolean(uid && supports3D), [uid, supports3D]);
  const isSupportAR = useMemo(() => Boolean(uid && supportsAR), [uid, supportsAR]);

  const galleryList = useMemo(() => {
    const threedImages =
      selectedVariant?.threed_images && !uid ? { type: '3d', ...selectedVariant.threed_images[0] } : null;
    const sortedImages = mergeAndSortArrays(selectedVariant?.images, selectedVariant?.assets || []);
    if (threedImages) {
      const baseIndex = sortedImages.findIndex((item) => item.type === 'base' || item.type === 'base_old');
      if (baseIndex !== -1) {
        sortedImages.splice(baseIndex, 1, sortedImages[baseIndex], threedImages);
      } else {
        sortedImages.splice(1, 0, threedImages);
      }
    }
    return handleImagesSort(sortedImages);
  }, [selectedVariant?.assets, selectedVariant?.images, selectedVariant.threed_images, uid]);

  const { masterVideo, masterVideoIndex } = useMemo(() => {
    const masterVideoIndex = galleryList?.findIndex((item) => item.type === 'master_video') ?? -1;
    const masterVideo = masterVideoIndex !== -1 ? galleryList[masterVideoIndex] : undefined;
    return { masterVideo, masterVideoIndex };
  }, [galleryList]);

  const overLayList = useMemo(() => {
    return (
      product?.bundle_options
        ?.map((item) => {
          const searchValue = searchParams.get(item?.name);
          if (searchValue !== null) {
            const matchedVariant = item?.variants?.find((variant) => `${variant.id}` === searchValue);
            if (matchedVariant?.overlay?.links?.large_overlay) {
              return matchedVariant?.overlay?.links?.large_overlay;
            }
          }
          return null;
        })
        .filter((item): item is string => item !== null)
        ?.map((item) => item.split('-test').join('')) || []
    );
  }, [searchParams, product?.bundle_options]);

  const targetLinks = useMemo(() => {
    return selectedVariant?.dimension_image?.links || product?.dimension_image?.links;
  }, [selectedVariant?.dimension_image?.links, product?.dimension_image?.links]);

  const { dimensionImage, dimensionGrayImage } = useMemo(() => {
    return {
      dimensionImage: targetLinks?.feed,
      dimensionGrayImage: targetLinks?.large_gray,
    };
  }, [targetLinks]);

  useSlickAutoScroll({
    swiperRef: mainSwiperRef,
    images: galleryList,
    product,
    variantId: selectedVariant?.id,
    bundleVariants,
  });
  const trackImageImpression = useCallback(
    async (target: any) => {
      await dispatch(EVENT_PDP_IMAGE_IMPRESSION({ assetPosition: currentIndex + 1, assetType: target.type }));
    },
    [currentIndex, dispatch]
  );

  // 图片曝光事件
  useEffect(() => {
    const target = galleryList[currentIndex];
    if (target) {
      trackImageImpression(target);
    }
  }, [galleryList, currentIndex, dispatch, trackImageImpression]);

  // Hash 操作函数
  const handleAddHash = useCallback((hash: string) => {
    // let currentUrl = window.location.href;

    // if (currentUrl.indexOf('#') !== -1) {
    //   const parts = currentUrl.split('#');
    //   currentUrl = `${parts[0]}#${hash}${parts[1] ? `#${parts[1]}` : ''}`;
    // } else {
    //   currentUrl += `#${hash}`;
    // }
    // window.history.pushState({}, document.title, currentUrl);
    // 获取不含 hash 的基础 URL
    window.location.hash = hash;
  }, []);

  const handleTrackPDPDetails = useCallback(
    async ({ action, label }: { action: string; label: string }) => {
      await dispatch(
        EVENT_PDP_DETAILS({
          action,
          label,
        })
      );
    },
    [dispatch]
  );

  const vrButtonsRef = useFirstInView(() => {
    // view_impression
    if (supports3D) {
      handleTrackPDPDetails({ action: 'view_impression', label: '360_view' });
    }
    if (supportsAR) {
      handleTrackPDPDetails({ action: 'view_impression', label: 'view_with_ar' });
    }
  });

  // 3D 查看处理
  const handleThreeDView = useCallback(
    (isShow: boolean) => {
      setShowThreeD(isShow);
      if (isShow) {
        setDefaultStartAR(false);
        handleTrackPDPDetails({ action: 'view_click', label: '360_view' });
      }
    },
    [handleTrackPDPDetails]
  );

  // AR 查看处理 - 参考原始 SketchfabViewer.js 的 handleAR 逻辑
  const handleARView = useCallback(() => {
    // TODO: 添加事件追踪
    handleTrackPDPDetails({ action: 'view_click', label: 'view_with_ar' });

    if (!desktop) {
      // 移动端：修改 hash 并启动 3D 查看器
      handleAddHash('ar-via-qr-code');
      setShowThreeD(true);
      setDefaultStartAR(true);
    } else {
      // 桌面端：直接打开 AR drawer 显示二维码
      setShowARDrawer(true);
    }
  }, [desktop, handleAddHash]);

  // 客户端 URL 初始化
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const fullUrl = `${window.location.origin}${pathname}${
        searchParams.toString() ? `?${searchParams.toString()}` : ''
      }`;
      setCurrentUrl(fullUrl);
    }
  }, [pathname, searchParams]);

  useEffect(() => {
    if (!uid || !hash) return;

    if (hash === 'dimensions-3d' && isSupportThreeD) {
      setShowThreeD(true);
      setDefaultStartAR(false);
    } else if (hash === 'ar-via-qr-code' && !desktop && isSupportAR) {
      setShowThreeD(true);
      setDefaultStartAR(true);
    }
    // else if (hash === 'web-ar-not-supported') {
    //   if (!desktop) {
    //     console.warn('AR not supported on this device');
    //   }
    // }
    // else if (hash && (hash === 'dimensions-3d' || hash === 'ar-via-qr-code')) {
    //   window.history.replaceState({}, document.title, window.location.pathname + window.location.search);
    // }
  }, [uid, hash, isSupportThreeD, isSupportAR, desktop, setShowThreeD, setDefaultStartAR]);

  // 变体信息（用于 Sketchfab 查看器）
  const variantInfo = useMemo(() => {
    if (!selectedVariant) return undefined;

    return {
      id: String(selectedVariant.id),
      name: selectedVariant.name,
      checkoutTitle: (selectedVariant as any)?.product_name || product?.name || '',
      checkoutSubtitle: selectedVariant.name,
      price: selectedVariant.price, // 这里保持原始类型，在 SketchfabViewer 中处理格式化
    };
  }, [selectedVariant, product]);

  const checkDimensionActive = useCallback((index: number) => dimensionActiveSet.has(index), [dimensionActiveSet]);

  const handleOnPictureClick = useCallback(
    (index: number) => {
      const touchDuration = touchEndTime - touchStartTime;
      const isQuickTouch = touchDuration < 200;

      if (isQuickTouch) {
        const currentMedia = galleryList?.[index];
        const isVideo = currentMedia && currentMedia.type?.includes('video');

        if (checkDimensionActive(index) && dimensionPinchZoomActive && !isVideo) {
          setDimensionPinchZoomOpen(true);
          return;
        }

        if (!isVideo) {
          setCurrentIndex(index);
          setOpen(true);
        }
      }
    },
    [
      touchEndTime,
      touchStartTime,
      galleryList,
      checkDimensionActive,
      dimensionPinchZoomActive,
      setDimensionPinchZoomOpen,
      setCurrentIndex,
      setOpen,
    ]
  );

  const handleViewMoreFeatures = useCallback(() => {
    if (masterVideoIndex !== -1 && mainSwiperRef.current) {
      mainSwiperRef.current.slideTo(masterVideoIndex);
      setCurrentIndex(masterVideoIndex);
      setTimeout(() => {
        const masterVideoRef = videoRefs.current.get(masterVideoIndex);
        if (masterVideoRef) {
          masterVideoRef.handleClickPreview?.();
          // setPlayingVideos((prev) => new Set(prev).add(masterVideoIndex));
        }
      }, 600);
    }
  }, [masterVideoIndex]);

  const scrollToDimension = useCallback(
    (isActive: boolean, index: number) => {
      setDimensionPinchZoomActive(isActive);

      if (isActive) {
        setDimensionActiveSet((prev) => new Set(prev).add(index));
      } else {
        setDimensionActiveSet((prev) => {
          const newSet = new Set(prev);
          newSet.delete(index);
          return newSet;
        });
      }

      if (!isActive || !desktop) return;
      const dimensionAnchor = document.getElementById('dimension-property');
      if (dimensionAnchor) {
        dimensionAnchor.scrollIntoView({ behavior: 'smooth', block: 'start' });
        handleAddHash('dimension-property');
        // // 手动触发 hashchange 事件，确保组件能立即响应
        // window.dispatchEvent(
        //   new HashChangeEvent('hashchange', {
        //     oldURL: window.location.href,
        //     newURL: `${window.location.pathname}${window.location.search}#dimension-property`,
        //   })
        // );
      }
    },
    [setDimensionPinchZoomActive, setDimensionActiveSet, desktop, handleAddHash]
  );

  const handleSlideChange = useCallback(
    (swiper: SwiperType) => {
      const newIndex = swiper.activeIndex;
      // setPlayingVideos(new Set());
      setCurrentIndex(newIndex);
      setIsDragging(false);
    },
    [setCurrentIndex, setIsDragging]
  );

  const handleVideoPlay = useCallback((index: number) => {
    // setPlayingVideos((prev) => new Set(prev).add(index));
  }, []);

  const handleVideoPause = useCallback((index: number) => {
    // setPlayingVideos((prev) => {
    //   const newSet = new Set(prev);
    //   newSet.delete(index);
    //   return newSet;
    // });
  }, []);

  const createVideoPlayHandler = useCallback((index: number) => () => handleVideoPlay(index), [handleVideoPlay]);
  const createVideoPauseHandler = useCallback((index: number) => () => handleVideoPause(index), [handleVideoPause]);

  const renderMediaItem = useCallback(
    (media: any, index: number, isCurrent: boolean) => (
      <MediaItem
        media={media}
        masterVideo={masterVideo}
        videoRefs={videoRefs}
        product={product}
        variant={selectedVariant!}
        index={index}
        isCurrent={isCurrent}
        onDimensionToggle={scrollToDimension}
        handleOnViewMoreFeatures={handleViewMoreFeatures}
        onVideoPlay={createVideoPlayHandler(index)}
        onVideoPause={createVideoPauseHandler(index)}
        galleryContainer={galleryRef.current}
      />
    ),
    [
      masterVideo,
      videoRefs,
      product,
      selectedVariant,
      scrollToDimension,
      handleViewMoreFeatures,
      createVideoPlayHandler,
      createVideoPauseHandler,
    ]
  );

  // AR/3D 按钮组件
  const renderARButtons = useCallback(() => {
    if (!isSupportThreeD) return null;

    return (
      <Stack
        flexDirection={mobile ? 'row' : 'column'}
        alignItems={mobile ? 'center' : 'stretch'}
        justifyContent="center"
        sx={(theme) => ({
          position: desktop || tablet ? 'absolute' : 'relative',
          maxWidth: '100%',
          bottom: desktop ? theme.spacing(6) : tablet ? theme.spacing(24) : 0,
          right: desktop || tablet ? theme.spacing(6) : 0,
          zIndex: 10,
          display: 'flex',
          gap: theme.spacing(3),
          px: mobile ? 6 : undefined,
          my: mobile ? 4 : undefined,
        })}
        ref={vrButtonsRef as any}
      >
        {/* 3D 查看按钮 */}
        <Button
          {...(mobile
            ? {
                variant: 'secondary',
              }
            : {
                imageButtonModule: true,
                variant: 'solid',
              })}
          sx={{
            ...(!isSupportAR && {
              width: '100%',
            }),
          }}
          startDecorator={<ThreeDRotation />}
          onClick={() => {
            handleAddHash('dimensions-3d');
            handleThreeDView(true);
          }}
        >
          <Typography level="subh2" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            360 View
          </Typography>
        </Button>
        {/* AR 按钮 - 参考原始逻辑 */}
        {isSupportAR && (
          <Button
            {...(mobile
              ? {
                  variant: 'secondary',
                }
              : {
                  imageButtonModule: true,
                  variant: 'solid',
                })}
            startDecorator={<ViewInAr />}
            onClick={handleARView}
          >
            <Typography level="subh2" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              View in AR
            </Typography>
          </Button>
        )}
      </Stack>
    );
  }, [isSupportThreeD, isSupportAR, handleARView, desktop, tablet, mobile, handleAddHash, handleThreeDView]);

  return (
    <>
      {galleryList?.length === 1 ? (
        <Box
          ref={galleryRef}
          sx={{
            position: 'relative',
            width: '100%',
            aspectRatio: mobile ? 0.75 : 1,
          }}
        >
          {renderMediaItem(galleryList[0], 0, true)}
          {/* AR/3D 按钮 */}
          {renderARButtons()}
        </Box>
      ) : (
        <Box
          ref={galleryRef}
          sx={{
            position: 'relative',
            width: '100%',
          }}
          onMouseEnter={() => desktop && setShowThumbnails(true)}
          onMouseLeave={() => desktop && setShowThumbnails(false)}
        >
          {desktop && (
            <Stack
              sx={{
                // width: '64px',
                height: '57%',
                position: 'absolute',
                left: 24,
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 10,
                opacity: showThumbnails ? 1 : 0,
                // visibility: showThumbnails ? 'visible' : 'hidden',
                transition: 'opacity 0.3s ease-in-out, visibility 0.3s ease-in-out',
                // overflow: 'hidden',
                '.swiper-wrapper': {
                  flexDirection: 'column',
                },
              }}
            >
              <Swiper
                onSwiper={(swiper) => (thumbsSwiperRef.current = swiper)}
                direction="vertical"
                spaceBetween={12}
                slidesPerView="auto"
                freeMode={true}
                watchSlidesProgress={true}
                grabCursor={true}
                mousewheel={{
                  forceToAxis: true,
                  sensitivity: 1,
                  releaseOnEdges: true,
                }}
                modules={[Thumbs, Mousewheel, FreeMode]}
                style={{ height: '100%' }}
                slideToClickedSlide={true}
                onClick={(swiper, event) => {
                  const clickedIndex = swiper.clickedIndex;
                  if (typeof clickedIndex === 'number' && mainSwiperRef.current) {
                    mainSwiperRef.current.slideTo(clickedIndex);
                  }
                }}
              >
                {galleryList?.map((media, index) => (
                  <SwiperSlide
                    key={index}
                    style={{
                      width: '64px',
                      height: '64px',
                      marginBottom: 24,
                    }}
                  >
                    <ThumbnailImage
                      media={media}
                      index={index}
                      product={product}
                      isActive={currentIndex === index}
                      dimensionGrayImage={dimensionGrayImage}
                    />
                  </SwiperSlide>
                ))}
              </Swiper>
            </Stack>
          )}
          <Box
            sx={{
              width: '100%',
            }}
          >
            <Swiper
              onSwiper={(swiper) => (mainSwiperRef.current = swiper)}
              direction={desktop ? 'vertical' : 'horizontal'}
              spaceBetween={0}
              slidesPerView={1}
              speed={desktop ? 500 : 300}
              grabCursor={true}
              touchRatio={desktop ? 1 : 0.8}
              longSwipesRatio={0.3}
              mousewheel={
                desktop
                  ? {
                      forceToAxis: true,
                      sensitivity: 1,
                      releaseOnEdges: true,
                    }
                  : false
              }
              freeMode={{
                enabled: true,
                sticky: true,
                momentumRatio: desktop ? 1 : 0.6,
                momentumVelocityRatio: desktop ? 1 : 0.4,
                momentumBounceRatio: desktop ? 1 : 0.3,
                minimumVelocity: desktop ? 0.02 : 0.08,
              }}
              thumbs={{
                swiper: thumbsSwiperRef.current && !thumbsSwiperRef.current.destroyed ? thumbsSwiperRef.current : null,
              }}
              modules={[Navigation, Mousewheel, Thumbs, FreeMode]}
              onSlideChange={handleSlideChange}
              onSlideChangeTransitionStart={() => setIsDragging(true)}
              onDragStart={() => setIsDragging(true)}
              onDragEnd={() => setIsDragging(false)}
              onTouchStart={() => {
                setTouchStartTime(Date.now());
                setIsDragging(true);
              }}
              onTouchEnd={() => {
                setTouchEndTime(Date.now());
                setIsDragging(false);
              }}
              onTransitionEnd={(swiper) => {
                setIsDragging(false);
                // handleTransitionEnd(swiper);
              }}
              onClick={(swiper, event) => {
                const clickedIndex = swiper.clickedIndex;
                if (typeof clickedIndex === 'number') {
                  const target = event?.target as HTMLElement;
                  const isRadioInput = target?.tagName === 'INPUT' && target?.getAttribute('type') === 'radio';
                  const isButton = target?.tagName === 'BUTTON' || target?.getAttribute('type') === 'button';
                  if (!isRadioInput && !isButton) {
                    handleOnPictureClick(clickedIndex);
                  }
                }
              }}
              style={{
                width: '100%',
                aspectRatio: mobile ? 0.75 : 1,
              }}
            >
              {galleryList?.map((media, index) => (
                <SwiperSlide key={index}>{renderMediaItem(media, index, currentIndex === index)}</SwiperSlide>
              ))}
            </Swiper>
            {!desktop && (
              <Box
                sx={{
                  width: '100%',
                  mt: 2,
                  '.swiper-scrollbar': {
                    '--swiper-scrollbar-bottom': 0,
                    backgroundColor: 'var(--fortress-palette-brand-mono-100)',
                    left: 0,
                    '.swiper-scrollbar-drag': {
                      '--swiper-scrollbar-drag-bg-color': 'var(--fortress-palette-brand-terracotta-500)',
                    },
                  },
                  pl: 2,
                  // '.swiper-scrollbar-drag': {
                  //   background: 'rgba(0, 0, 0, 0.3)',
                  //   borderRadius: '2px',
                  // },
                }}
              >
                <Swiper
                  onSwiper={(swiper) => (thumbsSwiperRef.current = swiper)}
                  spaceBetween={8}
                  slidesPerView="auto"
                  freeMode={true}
                  watchSlidesProgress={true}
                  scrollbar={{
                    hide: false,
                    draggable: true,
                    dragSize: 'auto',
                  }}
                  modules={[Thumbs, Scrollbar]}
                  slideToClickedSlide={true}
                  onClick={(swiper, event) => {
                    const clickedIndex = swiper.clickedIndex;
                    if (typeof clickedIndex === 'number' && mainSwiperRef.current) {
                      mainSwiperRef.current.slideTo(clickedIndex);
                    }
                  }}
                  style={{
                    paddingBottom: 12,
                  }}
                >
                  {galleryList?.map((media, index) => (
                    <SwiperSlide
                      key={index}
                      style={{
                        width: '64px',
                        height: '64px',
                      }}
                    >
                      <ThumbnailImage
                        media={media}
                        index={index}
                        product={product}
                        isActive={currentIndex === index}
                        dimensionGrayImage={dimensionGrayImage}
                      />
                    </SwiperSlide>
                  ))}
                </Swiper>
              </Box>
            )}
            {/* AR/3D 按钮 */}
            {renderARButtons()}
            {desktop && galleryList?.length >= 1 && currentIndex !== galleryList?.length - 1 && (
              <SwiperArrow onClick={() => mainSwiperRef.current?.slideNext()} galleryRef={galleryRef} />
            )}
          </Box>
          {showThreeD && uid && (
            <SketchfabViewer
              uid={uid}
              variantInfo={variantInfo}
              handleThreeDView={handleThreeDView}
              defaultStartAR={defaultStartAR}
              supportsAR={isSupportAR}
              onOpenARDrawer={() => setShowARDrawer(true)}
            />
          )}

          <ARDrawer
            open={showARDrawer}
            onClose={() => setShowARDrawer(false)}
            url={currentUrl}
            uid={uid || ''}
            api={null} // 暂时传 null，使用内部的 AR 模型获取逻辑
            variantInfo={variantInfo}
            onStartAR={(modelId: string) => {
              // TODO: 添加 AR 启动事件追踪
              console.log('AR started for model:', modelId);
            }}
            onError={(message: string) => {
              logger.error('AR error', { message });
            }}
            onBack={() => {
              setShowARDrawer(false);
              handleThreeDView(false);
            }}
          />
        </Box>
      )}
      <PinchZoomViewer
        open={open}
        setOpen={setOpen}
        slideImages={galleryList?.map((item, index) => {
          if (item.type?.includes('video')) {
            return {
              type: 'video',
              src: item.path || '',
              width: 100,
              height: 100,
              alt: `${item.type} ${index} video`,
              assetType: item.type,
              assetPosition: index + 1, // isDimensionActive && isDemissionType?"thumbnail button":index+1;
              controls: item.type === 'short_video' ? false : true,
            };
          }
          return {
            src: item.links?.large_gray || '',
            width: 100,
            height: 100,
            alt: `${item.type} ${index} image`,
            type: item.type,
            overLayList: item.type === 'base' || item.type === 'base_old' ? overLayList : [],
            assetType: item.type,
            assetPosition: index + 1,
          };
        })}
        index={currentIndex}
        enabledTrackSlider={true}
      />
      <PinchZoomViewer
        open={dimensionPinchZoomOpen}
        setOpen={setDimensionPinchZoomOpen}
        slideImages={[
          {
            src: dimensionGrayImage,
            width: 100,
            height: 100,
            alt: 'dimension gray image',
            assetType: 'dimension',
            assetPosition: 'product dimension',
          },
        ]}
        index={0}
        enabledTrackSlider={true}
      />
    </>
  );
}
